import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { logError } from "../lib/logger";
import { formatTL, formatDateTime } from "../lib/format";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Skeleton } from "./ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import {
  FileUp,
  Upload,
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Undo2,
  History,
  TrendingUp,
  Wallet,
  Package,
  Table2,
  Users,
  RotateCcw,
} from "lucide-react";

const TIPLER = [
  { key: "ciro", etiket: "Ciro", aciklama: "Gelir / fatura kayıtları", icon: TrendingUp },
  { key: "gider", etiket: "Gider", aciklama: "Faaliyet gider kayıtları (kalem eşleşmeli)", icon: Wallet },
  { key: "maliyet", etiket: "Maliyet (SMM)", aciklama: "Dosya bazlı maliyet kayıtları", icon: Package },
  { key: "butce", etiket: "Bütçe Planı", aciklama: "Kalem × 12 ay matris", icon: Table2 },
  { key: "ucret", etiket: "Ücret Geçmişi", aciklama: "Personel × ay, üç değer kolonu (KVKK)", icon: Users },
];

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

const ALANLAR = {
  ciro: [
    { key: "tarih", etiket: "Tarih", zorunlu: true },
    { key: "belge_turu", etiket: "Belge Türü" },
    { key: "musteri_aciklama", etiket: "Müşteri / Açıklama" },
    { key: "plaka_dosya_no", etiket: "Plaka / Dosya No" },
    { key: "belge_no", etiket: "Belge No" },
    { key: "matrah", etiket: "Matrah", zorunlu: true },
    { key: "kdv", etiket: "KDV" },
    { key: "toplam", etiket: "Toplam" },
    { key: "kanal", etiket: "Kanal" },
    { key: "bolum", etiket: "Bölüm" },
    { key: "tahsilat_durumu", etiket: "Tahsilat Durumu" },
    { key: "tahsilat_tarihi", etiket: "Tahsilat Tarihi" },
  ],
  gider: [
    { key: "tarih", etiket: "Tarih", zorunlu: true },
    { key: "kalem_adi", etiket: "Gider Kalemi", zorunlu: true },
    { key: "tedarikci", etiket: "Tedarikçi" },
    { key: "matrah", etiket: "Matrah", zorunlu: true },
    { key: "kdv", etiket: "KDV" },
    { key: "toplam", etiket: "Toplam" },
    { key: "belge_no", etiket: "Belge No" },
    { key: "odeme_sekli", etiket: "Ödeme Şekli" },
    { key: "aciklama", etiket: "Açıklama" },
  ],
  maliyet: [
    { key: "tarih", etiket: "Tarih", zorunlu: true },
    { key: "plaka_dosya_no", etiket: "Plaka / Dosya No" },
    { key: "tedarikci", etiket: "Tedarikçi" },
    { key: "matrah", etiket: "Matrah", zorunlu: true },
    { key: "kdv", etiket: "KDV" },
    { key: "toplam", etiket: "Toplam" },
    { key: "belge_no", etiket: "Belge No" },
    { key: "aciklama", etiket: "Açıklama" },
    { key: "kategori", etiket: "Kategori" },
    { key: "bolum", etiket: "Bölüm" },
  ],
  butce: [
    { key: "kalem_adi", etiket: "Kalem Adı", zorunlu: true },
    ...AYLAR.map((a, i) => ({ key: `ay_${i + 1}`, etiket: a })),
  ],
  ucret: [
    { key: "personel_adi", etiket: "Personel Adı", zorunlu: true },
    { key: "yil", etiket: "Yıl" },
    { key: "ay", etiket: "Ay", zorunlu: true },
    { key: "anlasilan_net", etiket: "Anlaşılan Net" },
    { key: "bordro_brut", etiket: "Bordro Brüt" },
    { key: "banka_net", etiket: "Banka Net" },
  ],
};

const TIP_ETIKET = Object.fromEntries(TIPLER.map((t) => [t.key, t.etiket]));

// Basit istemci tarafı otomatik ön-eşleme
const normalize = (s) =>
  String(s).toLocaleLowerCase("tr").replace(/[̇]/g, "").replace(/[^a-zçğıöşü0-9]/g, "");

const otomatikEsle = (tip, kolonlar) => {
  const sonuc = {};
  const kullanildi = new Set();
  ALANLAR[tip].forEach((alan) => {
    const hedef = normalize(alan.etiket) + "|" + normalize(alan.key);
    let bulunan = null;
    for (const k of kolonlar) {
      if (kullanildi.has(k)) continue;
      const nk = normalize(k);
      if (!nk) continue;
      if (hedef.includes(nk) || nk.includes(normalize(alan.etiket)) || nk === normalize(alan.key)) {
        bulunan = k;
        break;
      }
    }
    if (bulunan) {
      sonuc[alan.key] = bulunan;
      kullanildi.add(bulunan);
    }
  });
  return sonuc;
};

export const ImportWizard = () => {
  const [adim, setAdim] = useState(0); // 0 tip, 1 dosya, 2 eşleme, 3 analiz, 4 sonuç
  const [tip, setTip] = useState(null);
  const [parse, setParse] = useState(null);
  const [eslesme, setEslesme] = useState({});
  const [analiz, setAnaliz] = useState(null);
  const [onaylar, setOnaylar] = useState({});
  const [mukerrerDahil, setMukerrerDahil] = useState(false);
  const [varsayilanYil, setVarsayilanYil] = useState("2026");
  const [sonuc, setSonuc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hedefler, setHedefler] = useState([]); // kalem veya personel listesi
  const fileRef = useRef(null);

  const sifirla = () => {
    setAdim(0);
    setTip(null);
    setParse(null);
    setEslesme({});
    setAnaliz(null);
    setOnaylar({});
    setMukerrerDahil(false);
    setSonuc(null);
  };

  const tipSec = (t) => {
    setTip(t);
    setAdim(1);
  };

  const dosyaYukle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/aktarim/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParse(res.data);
      setEslesme(otomatikEsle(tip, res.data.kolonlar));
      setAdim(2);
    } catch (err) {
      toast.error(getErrMsg(err, "Dosya yüklenemedi."));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const analizYap = async () => {
    setBusy(true);
    try {
      const res = await api.post("/aktarim/analiz", {
        temp_id: parse.temp_id,
        tip,
        eslesme,
        varsayilan_yil: parseInt(varsayilanYil),
      });
      setAnaliz(res.data);
      const o = {};
      (res.data.eslesme_onerileri || []).forEach((e) => {
        if (e.oneri_id) o[e.dosya_adi] = e.oneri_id;
      });
      setOnaylar(o);
      // Hedef listeleri (onay Select'leri için)
      if (tip === "gider" || tip === "butce") {
        const k = await api.get("/butce-kalemleri");
        setHedefler(k.data.map((x) => ({ id: x.id, ad: x.kalem_adi })));
      } else if (tip === "ucret") {
        const p = await api.get("/personeller");
        setHedefler(p.data.map((x) => ({ id: x.id, ad: x.ad_soyad })));
      } else {
        setHedefler([]);
      }
      setAdim(3);
    } catch (err) {
      toast.error(getErrMsg(err, "Analiz yapılamadı."));
    } finally {
      setBusy(false);
    }
  };

  const uygula = async () => {
    setBusy(true);
    try {
      const res = await api.post("/aktarim/uygula", {
        temp_id: parse.temp_id,
        tip,
        eslesme,
        varsayilan_yil: parseInt(varsayilanYil),
        onaylar,
        mukerrer_dahil: mukerrerDahil,
      });
      setSonuc(res.data);
      setAdim(4);
      toast.success(`İçe aktarma tamamlandı: ${res.data.eklenen} kayıt eklendi.`);
    } catch (err) {
      toast.error(getErrMsg(err, "İçe aktarma başarısız."));
    } finally {
      setBusy(false);
    }
  };

  const zorunluEksik = useMemo(() => {
    if (!tip) return [];
    return ALANLAR[tip].filter((a) => a.zorunlu && !eslesme[a.key]).map((a) => a.etiket);
  }, [tip, eslesme]);

  return (
    <div className="space-y-4" data-testid="import-wizard">
      {/* Adım göstergesi */}
      <div className="flex items-center gap-1.5 text-[11px]" data-testid="import-adimlar">
        {["Veri Tipi", "Dosya", "Kolon Eşleme", "Önizleme & Analiz", "Sonuç"].map((a, i) => (
          <div key={a} className="flex items-center gap-1.5">
            <span
              className={`flex h-6 w-6 items-center justify-center rounded-full font-bold ${
                i < adim ? "bg-emerald-100 text-emerald-700" : i === adim ? "bg-[hsl(var(--accent))] text-[hsl(var(--primary))]" : "bg-muted text-muted-foreground"
              }`}
            >
              {i < adim ? "✓" : i + 1}
            </span>
            <span className={i === adim ? "font-semibold" : "text-muted-foreground"}>{a}</span>
            {i < 4 && <span className="mx-0.5 text-muted-foreground/40">—</span>}
          </div>
        ))}
      </div>

      {/* ADIM 0: TİP SEÇİMİ */}
      {adim === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="import-tip-secimi">
          {TIPLER.map((t) => (
            <button
              key={t.key}
              onClick={() => tipSec(t.key)}
              className="rounded-xl border bg-card p-4 text-left transition-transform duration-150 hover:-translate-y-0.5 hover:border-[hsl(var(--accent))]/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))]"
              data-testid={`import-tip-${t.key}`}
            >
              <t.icon className="h-5 w-5 text-[hsl(var(--accent))]" />
              <p className="mt-2 font-display text-sm font-semibold">{t.etiket}</p>
              <p className="text-[11px] text-muted-foreground">{t.aciklama}</p>
            </button>
          ))}
        </div>
      )}

      {/* ADIM 1: DOSYA */}
      {adim === 1 && (
        <div className="space-y-3" data-testid="import-dosya-adimi">
          <Badge className="bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/15 hover:bg-[hsl(var(--primary))]/5">
            Tip: {TIP_ETIKET[tip]}
          </Badge>
          <label
            className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/30 p-8 text-center transition-colors hover:border-[hsl(var(--accent))]/60"
            data-testid="import-dosya-alani"
          >
            <FileUp className="h-8 w-8 text-muted-foreground" />
            <span className="text-sm font-medium">XLSX veya CSV dosyası seçin</span>
            <span className="text-xs text-muted-foreground">En fazla 10 MB / 20.000 satır</span>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => dosyaYukle(e.target.files?.[0])}
              data-testid="import-dosya-input"
            />
            {busy && <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--accent))]" />}
          </label>
          <Button variant="outline" onClick={sifirla} className="gap-1.5" data-testid="import-geri-tip">
            <ArrowLeft className="h-4 w-4" /> Tip Seçimine Dön
          </Button>
        </div>
      )}

      {/* ADIM 2: KOLON EŞLEME */}
      {adim === 2 && parse && (
        <div className="space-y-4" data-testid="import-esleme-adimi">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge className="bg-[hsl(var(--primary))]/5 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/15 hover:bg-[hsl(var(--primary))]/5">
              {TIP_ETIKET[tip]}
            </Badge>
            <span className="text-muted-foreground text-xs">
              {parse.dosya_adi} • {parse.satir_sayisi.toLocaleString("tr-TR")} satır
            </span>
            {tip === "ucret" && (
              <div className="ml-auto flex items-center gap-2">
                <Label className="text-xs">Varsayılan Yıl</Label>
                <Input
                  type="number"
                  value={varsayilanYil}
                  onChange={(e) => setVarsayilanYil(e.target.value)}
                  className="h-9 w-24"
                  data-testid="import-varsayilan-yil"
                />
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              Hedef alan → dosya kolonu eşlemesi <span className="normal-case font-normal">(* zorunlu)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {ALANLAR[tip].map((alan) => (
                <div key={alan.key} className="space-y-1">
                  <Label className="text-xs">
                    {alan.etiket} {alan.zorunlu && <span className="text-red-600">*</span>}
                  </Label>
                  <Select
                    value={eslesme[alan.key] || "__yok__"}
                    onValueChange={(v) => setEslesme((e) => ({ ...e, [alan.key]: v === "__yok__" ? undefined : v }))}
                  >
                    <SelectTrigger className={`h-9 ${alan.zorunlu && !eslesme[alan.key] ? "border-red-300" : ""}`} data-testid={`import-esleme-${alan.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card max-h-64">
                      <SelectItem value="__yok__">— Eşleme yok —</SelectItem>
                      {parse.kolonlar.map((k) => (
                        <SelectItem key={k} value={k}>{k}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          {/* Önizleme */}
          <div className="rounded-xl border bg-card overflow-hidden">
            <p className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Dosya Önizleme (ilk 10 satır)</p>
            <div className="w-full overflow-x-auto p-2">
              <table className="w-full text-[11px]" data-testid="import-onizleme-tablo">
                <thead>
                  <tr className="bg-muted/50 text-left text-muted-foreground">
                    {parse.kolonlar.map((k) => (
                      <th key={k} className="px-2 py-1.5 whitespace-nowrap">{k}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parse.onizleme.map((row, i) => (
                    <tr key={i} className="border-t border-border/40">
                      {parse.kolonlar.map((k) => (
                        <td key={k} className="px-2 py-1.5 whitespace-nowrap max-w-[180px] truncate">
                          {row[k] == null ? "—" : String(row[k])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setAdim(1)} className="gap-1.5" data-testid="import-geri-dosya">
              <ArrowLeft className="h-4 w-4" /> Geri
            </Button>
            <Button onClick={analizYap} disabled={busy || zorunluEksik.length > 0} className="ml-auto gap-1.5 min-h-[44px]" data-testid="import-analiz-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Analiz Et
            </Button>
          </div>
          {zorunluEksik.length > 0 && (
            <p className="text-xs text-red-600" data-testid="import-zorunlu-uyari">
              Zorunlu alanları eşleyin: {zorunluEksik.join(", ")}
            </p>
          )}
        </div>
      )}

      {/* ADIM 3: ANALİZ SONUCU */}
      {adim === 3 && analiz && (
        <div className="space-y-4" data-testid="import-analiz-adimi">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SayiKutu etiket="Toplam Satır" deger={analiz.toplam_satir} renk="#2563EB" testid="import-sayi-toplam" />
            <SayiKutu etiket="Geçerli" deger={analiz.gecerli} renk="#16A34A" testid="import-sayi-gecerli" />
            <SayiKutu etiket="Hatalı (atlanacak)" deger={analiz.hata_sayisi} renk="#DC2626" testid="import-sayi-hata" />
            {tip === "ciro" ? (
              <SayiKutu etiket="Mükerrer" deger={analiz.mukerrer_sayisi} renk="#D97706" testid="import-sayi-mukerrer" />
            ) : (
              <SayiKutu etiket="Mevcut (atlanacak)" deger={analiz.mevcut_kayit_sayisi} renk="#D97706" testid="import-sayi-mevcut" />
            )}
          </div>

          {/* Hata raporu */}
          {analiz.hata_sayisi > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50/50 p-4" data-testid="import-hata-raporu">
              <p className="flex items-center gap-1.5 text-xs font-bold text-red-700">
                <AlertTriangle className="h-4 w-4" /> HATA RAPORU — bu satırlar atlanacak
              </p>
              <div className="mt-2 max-h-40 overflow-y-auto">
                <ul className="space-y-1">
                  {analiz.hatalar.map((h, i) => (
                    <li key={i} className="text-xs text-red-800">Satır {h.satir}: {h.sebep}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Mükerrer uyarısı (ciro) */}
          {tip === "ciro" && analiz.mukerrer_sayisi > 0 && (
            <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4" data-testid="import-mukerrer-uyari">
              <p className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                <Copy className="h-4 w-4" /> MÜKERRER UYARISI — aynı tarih + belge no + toplam
              </p>
              <div className="mt-2 max-h-40 overflow-y-auto">
                <ul className="space-y-1">
                  {analiz.mukerrerler.map((m, i) => (
                    <li key={i} className="text-xs text-amber-900">
                      Satır {m.satir}: {m.tarih} • {m.belge_no || "belge no yok"} • {formatTL(m.toplam)}
                    </li>
                  ))}
                </ul>
              </div>
              <label className="mt-3 flex items-center gap-2 text-xs font-medium cursor-pointer">
                <Checkbox checked={mukerrerDahil} onCheckedChange={(v) => setMukerrerDahil(!!v)} data-testid="import-mukerrer-checkbox" />
                Mükerrer satırları yine de içe aktar
              </label>
            </div>
          )}

          {/* Kalem / Personel eşleşme onayı */}
          {analiz.eslesme_onerileri.length > 0 && (
            <div className="rounded-xl border bg-card overflow-hidden" data-testid="import-eslesme-onay">
              <p className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {tip === "ucret" ? "Personel Eşleşmeleri" : "Gider Kalemi Eşleşmeleri"} — önerileri kontrol edin ve onaylayın
              </p>
              <div className="w-full overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                      <th className="px-4 py-2">Dosyadaki Ad</th>
                      <th className="px-3 py-2 text-right">Kayıt</th>
                      <th className="px-3 py-2">Eşleşecek {tip === "ucret" ? "Personel" : "Kalem"}</th>
                      <th className="px-3 py-2 text-right">Benzerlik</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analiz.eslesme_onerileri.map((o) => (
                      <tr key={o.dosya_adi} className="border-t border-border/50" data-testid={`import-onay-satir`}>
                        <td className="px-4 py-2 font-medium">{o.dosya_adi}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{o.adet}</td>
                        <td className="px-3 py-2 min-w-[220px]">
                          <Select
                            value={onaylar[o.dosya_adi] || "__atla__"}
                            onValueChange={(v) => setOnaylar((prev) => ({ ...prev, [o.dosya_adi]: v === "__atla__" ? undefined : v }))}
                          >
                            <SelectTrigger className={`h-9 ${!onaylar[o.dosya_adi] ? "border-amber-300" : ""}`} data-testid={`import-onay-select-${o.dosya_adi}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card max-h-64">
                              <SelectItem value="__atla__">— Atla (içe aktarma) —</SelectItem>
                              {hedefler.map((h) => (
                                <SelectItem key={h.id} value={h.id}>{h.ad}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {o.oneri_adi ? (
                            <Badge className={`${o.skor >= 80 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-300"} border hover:bg-transparent`}>
                              %{o.skor}
                            </Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-50">eşleşme yok</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setAdim(2)} className="gap-1.5" data-testid="import-geri-esleme">
              <ArrowLeft className="h-4 w-4" /> Eşlemeye Dön
            </Button>
            <Button onClick={uygula} disabled={busy || analiz.gecerli === 0} className="ml-auto gap-1.5 min-h-[44px] bg-emerald-600 hover:bg-emerald-700" data-testid="import-uygula-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              İçe Aktar
            </Button>
          </div>
        </div>
      )}

      {/* ADIM 4: SONUÇ */}
      {adim === 4 && sonuc && (
        <div className="space-y-4" data-testid="import-sonuc-adimi">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <p className="mt-2 font-display text-lg font-semibold text-emerald-800" data-testid="import-sonuc-baslik">
              İçe aktarma tamamlandı
            </p>
            <p className="mt-1 text-sm text-emerald-900" data-testid="import-sonuc-detay">
              <strong>{sonuc.eklenen}</strong> kayıt eklendi
              {sonuc.atlanan_hata > 0 && <> • {sonuc.atlanan_hata} hatalı satır atlandı</>}
              {sonuc.atlanan_mukerrer > 0 && <> • {sonuc.atlanan_mukerrer} mükerrer atlandı</>}
              {sonuc.atlanan_eslesmedi > 0 && <> • {sonuc.atlanan_eslesmedi} eşleşmeyen atlandı</>}
              {sonuc.atlanan_mevcut > 0 && <> • {sonuc.atlanan_mevcut} mevcut kayıt atlandı</>}
            </p>
            <p className="mt-1 text-[11px] text-emerald-700">
              Batch: {sonuc.id} — aşağıdaki geçmiş listesinden tek tıkla geri alabilirsiniz.
            </p>
          </div>
          <Button onClick={sifirla} className="gap-1.5" data-testid="import-yeni-button">
            <RotateCcw className="h-4 w-4" /> Yeni İçe Aktarma
          </Button>
        </div>
      )}

      {/* GEÇMİŞ + GERİ ALMA */}
      <ImportGecmisi yenile={adim === 4 || adim === 0} />
    </div>
  );
};

const SayiKutu = ({ etiket, deger, renk, testid }) => (
  <div className="rounded-xl border bg-card p-3.5" data-testid={testid}>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{etiket}</p>
    <p className="mt-1 font-display text-xl font-bold tabular-nums" style={{ color: renk }}>
      {Number(deger).toLocaleString("tr-TR")}
    </p>
  </div>
);

function ImportGecmisi({ yenile }) {
  const [batchler, setBatchler] = useState(null);
  const [geriAl, setGeriAl] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchBatchler = useCallback(async () => {
    try {
      const res = await api.get("/aktarim/batchler");
      setBatchler(res.data);
    } catch (err) {
      logError("Aktarım geçmişi yüklenemedi:", err);
    }
  }, []);

  useEffect(() => {
    fetchBatchler();
  }, [fetchBatchler, yenile]);

  const handleGeriAl = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/aktarim/batch/${geriAl.id}`);
      toast.success(res.data.message);
      setGeriAl(null);
      fetchBatchler();
    } catch (err) {
      toast.error(getErrMsg(err, "Geri alma başarısız."));
    } finally {
      setBusy(false);
    }
  };

  if (!batchler || batchler.length === 0) return null;

  return (
    <div className="rounded-xl border bg-card overflow-hidden" data-testid="import-gecmis">
      <p className="flex items-center gap-1.5 px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <History className="h-3.5 w-3.5" /> İçe Aktarma Geçmişi
      </p>
      <div className="w-full overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2">Tarih</th>
              <th className="px-3 py-2">Tip</th>
              <th className="px-3 py-2">Dosya</th>
              <th className="px-3 py-2 text-right">Eklenen</th>
              <th className="px-3 py-2 text-right">Atlanan</th>
              <th className="px-3 py-2">Durum</th>
              <th className="px-3 py-2 text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {batchler.map((b) => (
              <tr key={b.id} className="border-t border-border/50" data-testid="import-gecmis-satiri">
                <td className="px-4 py-2 whitespace-nowrap tabular-nums">{formatDateTime(b.tarih)}</td>
                <td className="px-3 py-2">{TIP_ETIKET[b.tip] || b.tip}</td>
                <td className="px-3 py-2 max-w-[180px] truncate">{b.dosya_adi}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{b.eklenen}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {(b.atlanan_hata || 0) + (b.atlanan_mukerrer || 0) + (b.atlanan_eslesmedi || 0) + (b.atlanan_mevcut || 0)}
                </td>
                <td className="px-3 py-2">
                  {b.geri_alindi ? (
                    <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Geri alındı</Badge>
                  ) : (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50">Aktif</Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {!b.geri_alindi && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 gap-1 text-xs text-red-600 hover:text-red-700"
                      onClick={() => setGeriAl(b)}
                      data-testid={`import-geri-al-${b.id}`}
                    >
                      <Undo2 className="h-3 w-3" /> Geri Al
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!geriAl} onOpenChange={(o) => !o && setGeriAl(null)}>
        <AlertDialogContent data-testid="import-geri-al-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>İçe aktarma geri alınsın mı?</AlertDialogTitle>
            <AlertDialogDescription>
              “{geriAl?.dosya_adi}” dosyasından eklenen {geriAl?.eklenen} kayıt kalıcı olarak silinecek
              {geriAl?.tip === "butce" ? " (bütçe değerleri önceki haline döndürülecek)" : ""}. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="import-geri-al-vazgec">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleGeriAl} disabled={busy} className="bg-red-600 hover:bg-red-700" data-testid="import-geri-al-onayla">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Geri Al"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
